
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useEffect } from 'react';
import type { Application } from '@/types/database.types';

interface MedicalEnrollmentInfoProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const MedicalEnrollmentInfo = ({
  register,
  watch,
  setValue,
  data
}: MedicalEnrollmentInfoProps) => {
  // Watch the coverage type fields for automatic calculation
  const employeeOnlyCount = watch('employee_only_count') || 0;
  const employeeSpouseCount = watch('employee_spouse_count') || 0;
  const employeeChildrenCount = watch('employee_children_count') || 0;
  const employeeFamilyCount = watch('employee_family_count') || 0;

  // Automatically calculate and update the total enrolled
  useEffect(() => {
    const total = Number(employeeOnlyCount) + Number(employeeSpouseCount) + Number(employeeChildrenCount) + Number(employeeFamilyCount);
    setValue('employees_enrolled_in_health_plan', total);
  }, [employeeOnlyCount, employeeSpouseCount, employeeChildrenCount, employeeFamilyCount, setValue]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Enrollment Information</h3>
      <Separator className="my-2" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="employee_only_count">
            Number of Employee Only
          </Label>
          <Input
            id="employee_only_count"
            type="number"
            min="0"
            defaultValue="0"
            {...register('employee_only_count', { 
              valueAsNumber: true
            })}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="employee_spouse_count">
            Number of Employee + Spouse
          </Label>
          <Input
            id="employee_spouse_count"
            type="number"
            min="0"
            defaultValue="0"
            {...register('employee_spouse_count', { 
              valueAsNumber: true
            })}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="employee_children_count">
            Number of Employee + Children
          </Label>
          <Input
            id="employee_children_count"
            type="number"
            min="0"
            defaultValue="0"
            {...register('employee_children_count', { 
              valueAsNumber: true
            })}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="employee_family_count">
            Number of Family
          </Label>
          <Input
            id="employee_family_count"
            type="number"
            min="0"
            defaultValue="0"
            {...register('employee_family_count', { 
              valueAsNumber: true
            })}
          />
        </div>
      </div>
      
      <div className="space-y-2 pt-4">
        <Label htmlFor="employees_enrolled_in_health_plan">
          Total Employees Enrolled in Health Plan
        </Label>
        <Input
          id="employees_enrolled_in_health_plan"
          type="number"
          min="0"
          readOnly
          className="bg-gray-50"
          {...register('employees_enrolled_in_health_plan', { 
            valueAsNumber: true
          })}
        />
      </div>
    </div>
  );
};
